import React from "react";
import { createPortal } from "react-dom";
import "./InstallAppBanner.css";
import { useAppUpdate } from "../app-update/useAppUpdate";
import { useInstallAppBanner } from "./useInstallAppBanner";

const DEFAULT_ANDROID_APK_URL =
  "https://chekhabare.com/downloads/chekhabare.apk";

function CloseIcon() {
  return (
    <svg
      aria-hidden="true"
      className="install-app-banner__close-icon"
      focusable="false"
      viewBox="0 0 24 24"
    >
      <path d="m6 6 12 12" />
      <path d="M18 6 6 18" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg
      aria-hidden="true"
      className="install-guide__step-icon"
      focusable="false"
      viewBox="0 0 32 32"
    >
      <path d="M16 4v16" />
      <path d="m10 10 6-6 6 6" />
      <path d="M9 15H6.8A2.8 2.8 0 0 0 4 17.8v7.4A2.8 2.8 0 0 0 6.8 28h18.4a2.8 2.8 0 0 0 2.8-2.8v-7.4a2.8 2.8 0 0 0-2.8-2.8H23" />
    </svg>
  );
}

function AddToHomeScreenIcon() {
  return (
    <svg
      aria-hidden="true"
      className="install-guide__step-icon"
      focusable="false"
      viewBox="0 0 32 32"
    >
      <rect
        height="24"
        rx="5"
        width="24"
        x="4"
        y="4"
      />
      <path d="M16 10v12" />
      <path d="M10 16h12" />
    </svg>
  );
}

function InstallBanner({ onDismiss, onInstall }) {
  return (
    <aside
      aria-label="نصب اپلیکیشن چه‌خبره"
      className="install-app-banner"
      dir="rtl"
    >
      <div className="install-app-banner__inner">
        <img
          alt=""
          aria-hidden="true"
          className="install-app-banner__logo"
          draggable="false"
          src={`${process.env.PUBLIC_URL}/logo192.png`}
        />

        <div className="install-app-banner__copy">
          <strong className="install-app-banner__title">
            اپلیکیشن چه‌خبره
          </strong>

          <span className="install-app-banner__subtitle">
            برای دسترسی سریع‌تر روی گوشی نصب کنید
          </span>
        </div>

        <button
          className="install-app-banner__install"
          onClick={onInstall}
          type="button"
        >
          نصب
        </button>

        <button
          aria-label="بستن بنر نصب"
          className="install-app-banner__close"
          onClick={onDismiss}
          type="button"
        >
          <CloseIcon />
        </button>
      </div>
    </aside>
  );
}


function UpdateBanner({
  isApplying,
  onDismiss,
  onUpdate,
}) {
  return (
    <aside
      aria-label="بروزرسانی چه‌خبره"
      className="install-app-banner install-app-banner--update"
      dir="rtl"
    >
      <div className="install-app-banner__inner">
        <img
          alt=""
          aria-hidden="true"
          className="install-app-banner__logo"
          draggable="false"
          src={`${process.env.PUBLIC_URL}/logo192.png`}
        />

        <div className="install-app-banner__copy">
          <strong className="install-app-banner__title">
            نسخه جدید چه‌خبره آماده است
          </strong>

          <span className="install-app-banner__subtitle">
            برای دریافت آخرین نسخه، بروزرسانی کنید
          </span>
        </div>

        <button
          aria-busy={isApplying ? "true" : undefined}
          className="install-app-banner__install install-app-banner__install--update"
          disabled={isApplying}
          onClick={onUpdate}
          type="button"
        >
          {isApplying ? "در حال..." : "بروزرسانی"}
        </button>

        <button
          aria-label="بستن بنر بروزرسانی"
          className="install-app-banner__close"
          disabled={isApplying}
          onClick={onDismiss}
          type="button"
        >
          <CloseIcon />
        </button>
      </div>
    </aside>
  );
}

function IosInstallSteps() {
  return (
    <ol className="install-guide__steps">
      <li className="install-guide__step">
        <p className="install-guide__step-text">
          <span className="install-guide__step-number">
            ۱-
          </span>
          در نوار پایین مرورگر روی دکمه{" "}
          <strong dir="ltr">Share</strong> بزنید.
        </p>

        <span className="install-guide__step-visual">
          <ShareIcon />
        </span>
      </li>

      <li className="install-guide__step">
        <p className="install-guide__step-text">
          <span className="install-guide__step-number">
            ۲-
          </span>
          در منوی بازشده، در قسمت پایین، گزینه{" "}
          <strong dir="ltr">Add to Home Screen</strong>{" "}
          را انتخاب کنید.
        </p>

        <span className="install-guide__step-visual">
          <AddToHomeScreenIcon />
        </span>
      </li>

      <li className="install-guide__step">
        <p className="install-guide__step-text">
          <span className="install-guide__step-number">
            ۳-
          </span>
          در مرحله بعد در قسمت بالا روی{" "}
          <strong dir="ltr">Add</strong> بزنید.
        </p>

        <span className="install-guide__step-visual">
          <span
            aria-hidden="true"
            className="install-guide__add-label"
            dir="ltr"
          >
            Add
          </span>
        </span>
      </li>
    </ol>
  );
}

function InstallGuide({
  androidApkUrl,
  onAndroidDownload,
  onClose,
  platform,
}) {
  const isIos = platform === "ios";

  const handleOverlayClick = (event) => {
    if (
      platform === "android" &&
      event.target === event.currentTarget
    ) {
      onClose();
    }
  };

  return (
    <section
      aria-labelledby="install-guide-title"
      aria-modal="true"
      className="install-guide"
      dir="rtl"
      onClick={handleOverlayClick}
      role="dialog"
    >
      <div className="install-guide__panel">
        <header className="install-guide__header">
          <img
            alt="لوگوی چه‌خبره"
            className="install-guide__logo"
            draggable="false"
            src={`${process.env.PUBLIC_URL}/logo192.png`}
          />

          <div className="install-guide__heading-copy">
            <h2
              className="install-guide__title"
              id="install-guide-title"
            >
              {isIos
                ? "نصب اپلیکیشن چه‌خبره"
                : "دانلود اپلیکیشن چه‌خبره"}
            </h2>

            <p className="install-guide__subtitle">
              {isIos
                ? "برای افزودن وب‌اپلیکیشن چه‌خبره به صفحه اصلی، این مراحل را انجام دهید."
                : "نسخه اندروید را مستقیم روی گوشی دانلود کنید."}
            </p>
          </div>
        </header>

        {isIos ? (
          <>
            <IosInstallSteps />

            <button
              className="install-guide__close"
              onClick={onClose}
              type="button"
            >
              متوجه شدم
            </button>
          </>
        ) : (
          <div className="install-guide__android-download">
            <p className="install-guide__android-text">
              برای دریافت نسخه رسمی چه‌خبره، روی دکمه زیر
              بزنید.
            </p>

            <a
              className="install-guide__download"
              download
              href={androidApkUrl}
              onClick={onAndroidDownload}
            >
              دانلود برنامه
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

function InstallAppBanner({ children }) {
  const {
    closeGuide,
    dismiss,
    isGuideVisible,
    isVisible,
    markAndroidDownloadStarted,
    platform,
    requestInstall,
  } = useInstallAppBanner();

  const {
    dismiss: dismissUpdate,
    isApplying: isApplyingUpdate,
    isUpdateAvailable,
    isVisible: isUpdateVisible,
    requestUpdate,
  } = useAppUpdate();

  const configuredApkUrl = String(
    process.env.REACT_APP_ANDROID_APK_URL ||
      DEFAULT_ANDROID_APK_URL
  ).trim();

  const androidApkUrl =
    configuredApkUrl || DEFAULT_ANDROID_APK_URL;

  const canUsePortal = typeof document !== "undefined";

  return (
    <>
      {children}

      {isUpdateVisible ? (
        <UpdateBanner
          isApplying={isApplyingUpdate}
          onDismiss={dismissUpdate}
          onUpdate={requestUpdate}
        />
      ) : (
        !isUpdateAvailable &&
        isVisible && (
          <InstallBanner
            onDismiss={dismiss}
            onInstall={requestInstall}
          />
        )
      )}

      {isGuideVisible &&
        canUsePortal &&
        createPortal(
          <InstallGuide
            androidApkUrl={androidApkUrl}
            onAndroidDownload={markAndroidDownloadStarted}
            onClose={closeGuide}
            platform={platform}
          />,
          document.body
        )}
    </>
  );
}

export default InstallAppBanner;
